import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { HelixLoader } from "@/components/ui/helix-loader";
import CourseSelector from "@/components/CourseSelector";


interface Course {
  id: string;
  slug: string;
  title: string;
}

interface CourseProgress {
  completed: number;
  total: number;
  percentage: number;
}

export default function UnifiedLearningPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [checkingCommitment, setCheckingCommitment] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseProgress, setCourseProgress] = useState<Record<string, CourseProgress>>({});
  const [lastActiveCourseId, setLastActiveCourseId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    initPage();
  }, []);

  const initPage = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/");
      return;
    }

    setUserId(session.user.id);
    const { data: profile } = await supabase
      .from('profiles')
      .select('current_course')
      .eq('id', session.user.id)
      .maybeSingle();

    await Promise.all([
      loadCourses(),
      loadCourseProgress(session.user.id),
    ]);
    setLoading(false);

    const isChangingCourse = searchParams.get('change') === 'true';

    if (profile?.current_course && !isChangingCourse) {
      navigate("/home");
      return;
    }
    setCheckingCommitment(false);
  };

  const loadCourses = async () => {
    const { data, error } = await supabase.functions.invoke("courses");
    if (error || !data || !Array.isArray(data)) return;

    const courseList: Course[] = [...data];
    const requiredSlugs = ["astronomy", "astrophysics", "quantum-mechanics"];
    const missingSlugs = requiredSlugs.filter((slug) => !courseList.some((course) => course.slug === slug));

    if (missingSlugs.length > 0) {
      const { data: fetchedCourses, error: fetchError } = await supabase.functions.invoke("courses", {
        body: { slugs: missingSlugs }
      });

      if (!fetchError && Array.isArray(fetchedCourses)) {
        fetchedCourses.forEach((course: Course) => {
          courseList.push(course);
        });
      }
    }

    const uniqueBySlug = new Map<string, Course>();
    courseList.forEach((course) => {
      uniqueBySlug.set(course.slug, course);
    });

    const allCourses = Array.from(uniqueBySlug.values());

    const titleIncludes = (course: Course, token: string) => course.title.toLowerCase().includes(token);

    const astronomyCourse =
      allCourses.find((course) => course.slug === "astronomy") ??
      allCourses.find((course) => titleIncludes(course, "astronomy")) ??
      null;

    const physicsCourse =
      allCourses.find((course) => course.slug === "physics") ??
      allCourses.find((course) => course.slug === "basic-physics") ??
      allCourses.find((course) => titleIncludes(course, "physics") && !titleIncludes(course, "quantum")) ??
      allCourses.find((course) => titleIncludes(course, "physics")) ??
      null;

    const astrophysicsCourse =
      allCourses.find((course) => course.slug === "astrophysics") ??
      allCourses.find((course) => titleIncludes(course, "astrophysics")) ??
      null;

    const quantumCourse =
      allCourses.find((course) => course.slug === "quantum-mechanics") ??
      allCourses.find((course) => titleIncludes(course, "quantum") || titleIncludes(course, "quantom")) ??
      null;

    const selectorCourses: Course[] = [
      astronomyCourse ?? { id: "astronomy", slug: "astronomy", title: "Astronomy" },
      physicsCourse ?? { id: "physics", slug: "physics", title: "Physics" },
      astrophysicsCourse ?? { id: "astrophysics", slug: "astrophysics", title: "Astrophysics" },
      quantumCourse ?? { id: "quantum-mechanics", slug: "quantum-mechanics", title: "Quantum Mechanics" },
    ];

    setCourses(selectorCourses);
  };

  const loadCourseProgress = async (uid: string) => {
    const { data: progressData } = await supabase
      .from("user_progress")
      .select("lesson_id, status")
      .eq("user_id", uid);

    const { data: lessonsData } = await supabase
      .from("lessons")
      .select("id, course_id, is_placeholder")
      .order("order_index", { ascending: true });

    if (lessonsData && progressData) {
      const progressMap: Record<string, CourseProgress> = {};
      const lessonsByCourse = lessonsData.reduce((acc, lesson) => {
        if (lesson.is_placeholder) return acc;
        if (!acc[lesson.course_id]) acc[lesson.course_id] = [];
        acc[lesson.course_id].push(lesson.id);
        return acc;
      }, {} as Record<string, string[]>);

      Object.entries(lessonsByCourse).forEach(([courseId, lessonIds]) => {
        const completed = lessonIds.filter((lid) =>
          progressData.some((p: any) => p.lesson_id === lid && p.status === "completed"),
        ).length;
        progressMap[courseId] = {
          completed,
          total: lessonIds.length,
          percentage: lessonIds.length > 0 ? Math.round((completed / lessonIds.length) * 100) : 0,
        };
      });
      setCourseProgress(progressMap);
    }
  };

  const handleStartCourseFromCourse = async (course: Course, isStarted: boolean = false) => {
    if (!userId) {
      navigate("/");
      return;
    }

    await supabase
      .from('profiles')
      .update({ current_course: course.id })
      .eq('id', userId);

    try {
      void isStarted;
      navigate(`/learning/${course.slug}`);
    } catch (error) {
      console.error("Error in handleStartCourse:", error);
      navigate(`/learning/${course.slug}`);
    }
  };

  const handleStartCourse = async (courseId: string, courseSlug: string) => {
    const course = courses.find((c) => c.id === courseId) ?? courses.find((c) => c.slug === courseSlug);
    if (!course) {
      navigate(`/learning/${courseSlug}`);
      return;
    }

    const progress = courseProgress[course.id] || { completed: 0, total: 0, percentage: 0 };
    const isStarted = progress.completed > 0;
    await handleStartCourseFromCourse(course, isStarted);
  };

  useEffect(() => {
    if (!courses.length) return;

    const activeCourses = courses.filter((course) => (courseProgress[course.id]?.completed ?? 0) > 0);
    if (!activeCourses.length) return;

    try {
      const storedId = localStorage.getItem("scico_last_course_id");
      const storedSlug = localStorage.getItem("scico_last_course_slug");

      if (storedId) {
        const storedActive = activeCourses.find((course) => course.id === storedId);
        if (storedActive) {
          setLastActiveCourseId(storedActive.id);
          return;
        }
      }

      if (storedSlug) {
        const storedActive = activeCourses.find((course) => course.slug === storedSlug);
        if (storedActive) {
          setLastActiveCourseId(storedActive.id);
          return;
        }
      }
    } catch {
      // ignore
    }

    setLastActiveCourseId(activeCourses[0].id);
  }, [courses, courseProgress]);

  if (loading || checkingCommitment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <HelixLoader className="text-primary" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      <CourseSelector courses={courses} onSelectCourse={handleStartCourse} lastActiveCourseId={lastActiveCourseId} />
    </div>
  );
}
